/**
 * Pure helpers for Food Safety oversight home (date clamp + status copy).
 */

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {
  FOOD_SAFETY_HOME_STATUS,
  FOOD_SAFETY_HOME_STATUS_LABELS,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_SECTION_LABELS,
  FOOD_SAFETY_SECTION_LOG_MODE,
  FOOD_SAFETY_SECTION_LOG_MODE_IDLE_COPY,
  FOOD_SAFETY_SECTION_LOG_MODE_TAGS,
} from 'src/constants/foodSafety'
import { formatCoolingOpenBatchesLine } from 'src/utils/foodSafetyCooling'
import { formatFridgeStillToLogFromLabels } from 'src/utils/foodSafetyFridges'
import { formatStillNeededHomeDetail } from 'src/utils/foodSafetyStillNeeded'

dayjs.extend(customParseFormat)

const LOCAL_RE = /^\d{4}-\d{2}-\d{2}$/

export const isFoodSafetyLocalDate = value =>
  typeof value === 'string' &&
  LOCAL_RE.test(value) &&
  dayjs(value, 'YYYY-MM-DD', true).isValid()

export const shiftFoodSafetyLocalDate = (localDate, deltaDays) => {
  if (!isFoodSafetyLocalDate(localDate)) return null
  return dayjs(localDate, 'YYYY-MM-DD')
    .add(Number(deltaDays) || 0, 'day')
    .format('YYYY-MM-DD')
}

/**
 * Clamp a requested date into the Free window (or unbounded when unlocked).
 * @param {string} localDate
 * @param {{
 *   fullHistoryUnlocked?: boolean,
 *   earliestLocalDate?: string|null,
 *   latestLocalDate?: string|null,
 * }} [window]
 */
export const clampFoodSafetyBrowseDate = (localDate, window = {}) => {
  if (!isFoodSafetyLocalDate(localDate)) return null
  let next = localDate
  if (
    !window.fullHistoryUnlocked &&
    window.earliestLocalDate &&
    isFoodSafetyLocalDate(window.earliestLocalDate) &&
    next < window.earliestLocalDate
  ) {
    next = window.earliestLocalDate
  }
  if (
    window.latestLocalDate &&
    isFoodSafetyLocalDate(window.latestLocalDate) &&
    next > window.latestLocalDate
  ) {
    next = window.latestLocalDate
  }
  return next
}

export const canBrowseFoodSafetyPrev = (localDate, window = {}) => {
  if (!isFoodSafetyLocalDate(localDate)) return false
  if (window.fullHistoryUnlocked) return true
  if (!window.earliestLocalDate) return true
  return localDate > window.earliestLocalDate
}

export const canBrowseFoodSafetyNext = (localDate, window = {}) => {
  if (!isFoodSafetyLocalDate(localDate)) return false
  if (window.fullHistoryUnlocked) return true
  const latest = window.latestLocalDate
  if (!latest) return true
  return localDate < latest
}

/**
 * Free 14-day edge: previous stays tappable so the Starter history paywall can open
 * (FoodSafetyDayNav `prevOpensPaywall` — not a silent disabled button).
 */
export const foodSafetyDayPrevOpensPaywall = (localDate, window = {}) =>
  Boolean(
    localDate &&
      !window.fullHistoryUnlocked &&
      !canBrowseFoodSafetyPrev(localDate, window),
  )

/**
 * Normalise historyAccess (home or day payload) into a browse window.
 * @param {{
 *   fullHistoryUnlocked?: boolean,
 *   window?: { earliestLocalDate?: string|null, latestLocalDate?: string|null },
 * }|null|undefined} historyAccess
 * @param {string|null|undefined} localDate fallback latest when window omits it
 */
export const buildFoodSafetyBrowseWindow = (
  historyAccess,
  localDate = null,
) => ({
  fullHistoryUnlocked: Boolean(historyAccess?.fullHistoryUnlocked),
  earliestLocalDate: historyAccess?.window?.earliestLocalDate || null,
  latestLocalDate: historyAccess?.window?.latestLocalDate || localDate || null,
})

const FOOD_SAFETY_EVENT_DRIVEN_BY_DEFAULT = new Set([
  FOOD_SAFETY_SECTION.COOKING_REHEATING,
  FOOD_SAFETY_SECTION.COOLING,
  FOOD_SAFETY_SECTION.DELIVERIES,
  FOOD_SAFETY_SECTION.HOT_HOLDING,
  FOOD_SAFETY_SECTION.COLD_HOLDING,
  FOOD_SAFETY_SECTION.PROBE_CALIBRATION,
  FOOD_SAFETY_SECTION.DISHWASHERS,
])

/**
 * Scheduled vs event-driven (logged when it happens). Express sends
 * `logMode`; rows without it fall back to the section's default.
 */
export const resolveFoodSafetySectionLogMode = (section = {}) => {
  if (Object.values(FOOD_SAFETY_SECTION_LOG_MODE).includes(section?.logMode)) {
    return section.logMode
  }
  return FOOD_SAFETY_EVENT_DRIVEN_BY_DEFAULT.has(section?.section)
    ? FOOD_SAFETY_SECTION_LOG_MODE.EVENT_DRIVEN
    : FOOD_SAFETY_SECTION_LOG_MODE.SCHEDULED
}

export const isFoodSafetySectionEventDriven = section =>
  resolveFoodSafetySectionLogMode(section) ===
  FOOD_SAFETY_SECTION_LOG_MODE.EVENT_DRIVEN

/** "Scheduled" / "As it happens" tag for a Home tile. */
export const formatFoodSafetySectionModeTag = section =>
  FOOD_SAFETY_SECTION_LOG_MODE_TAGS[resolveFoodSafetySectionLogMode(section)]

/** Open exceptions on a tile (any day) — "2 unresolved", or null. */
export const formatFoodSafetySectionUnresolved = section => {
  const n = Number(section?.unresolvedExceptionCount) || 0
  return n > 0 ? `${n} unresolved` : null
}

/** Sections with open exceptions, in tile order — for the Home strip. */
export const listFoodSafetyHomeUnresolved = sections =>
  (sections || [])
    .map(section => ({
      section: section.section,
      label:
        section.label ||
        FOOD_SAFETY_SECTION_LABELS[section.section] ||
        section.section,
      count: Number(section.unresolvedExceptionCount) || 0,
    }))
    .filter(row => row.count > 0)

/** Idle line for event-driven sections, e.g. "No entries today". */
export const formatFoodSafetyNoEntriesCue = ({ viewingToday = true } = {}) =>
  viewingToday ? 'No entries today' : 'No entries this day'

export const formatFoodSafetyHomeStatus = (
  status,
  section = {},
  { viewingToday = true, closed = false } = {},
) => {
  if (
    closed &&
    status !== FOOD_SAFETY_HOME_STATUS.ALL_DONE &&
    status !== FOOD_SAFETY_HOME_STATUS.IN_PROGRESS &&
    status !== FOOD_SAFETY_HOME_STATUS.ATTENTION
  ) {
    return 'Closed'
  }
  if (status === FOOD_SAFETY_HOME_STATUS.IDLE) {
    return FOOD_SAFETY_SECTION_LOG_MODE_IDLE_COPY[
      resolveFoodSafetySectionLogMode(section)
    ]
  }
  // Ad-hoc activity is not a scheduled "all clear" — avoid fake success language.
  if (status === FOOD_SAFETY_HOME_STATUS.ALL_DONE) {
    const dueCount = Number(section.dueCount) || 0
    if (dueCount === 0) return viewingToday ? 'Logged today' : 'Logged this day'
    return FOOD_SAFETY_HOME_STATUS_LABELS[FOOD_SAFETY_HOME_STATUS.ALL_DONE]
  }
  return FOOD_SAFETY_HOME_STATUS_LABELS[status] || 'Status unknown'
}

/**
 * Human date for section headers (e.g. Wed 23 Sep 2026).
 */
export const formatFoodSafetyDisplayDate = localDate => {
  if (!isFoodSafetyLocalDate(localDate)) return localDate || ''
  return dayjs(localDate, 'YYYY-MM-DD').format('ddd D MMM YYYY')
}

/**
 * True when the browsed operational day is today (29.43).
 * Unknown dates default to today so first paint stays calm.
 */
export const isFoodSafetyViewingToday = (localDate, todayLocalDate) => {
  const day = String(localDate || '').trim()
  const today = String(todayLocalDate || '').trim()
  if (!day || !today) return true
  return day === today
}

/** CTA on the past/future day strip (29.71). */
export const FOOD_SAFETY_BACK_TO_TODAY_LABEL = 'Back to today'

/**
 * Quiet “Viewing Wed 23 Sep” cue when Home is not on today (29.71).
 * Returns null when viewing today or dates are unknown.
 */
export const formatFoodSafetyViewingDayCue = (localDate, todayLocalDate) => {
  if (isFoodSafetyViewingToday(localDate, todayLocalDate)) return null
  if (!isFoodSafetyLocalDate(localDate)) return null
  return `Viewing ${formatFoodSafetyDisplayDate(localDate)}`
}

/**
 * Show Back to today whenever the browsed day ≠ kitchen today (29.71).
 */
export const shouldShowFoodSafetyBackToToday = (localDate, todayLocalDate) => {
  if (
    !isFoodSafetyLocalDate(localDate) ||
    !isFoodSafetyLocalDate(todayLocalDate)
  ) {
    return false
  }
  return localDate !== todayLocalDate
}

/**
 * Home date query/param for midnight hygiene (29.72).
 * Returns null when viewing today — callers omit `?date=` / clear route params
 * so a refresh after midnight loads true today.
 */
export const resolveFoodSafetyHomeDateParam = (localDate, todayLocalDate) => {
  if (!isFoodSafetyLocalDate(localDate)) return null
  if (isFoodSafetyViewingToday(localDate, todayLocalDate)) return null
  return localDate
}

const withDateParam = (path, localDate, todayLocalDate) => {
  const pinned = resolveFoodSafetyHomeDateParam(localDate, todayLocalDate)
  return pinned ? `${path}?date=${pinned}` : path
}

/**
 * Home (overview) link. Pins `?date=` only for another day, so a screen left
 * open past midnight follows the kitchen's today (38.15).
 */
export const foodSafetyHomeHref = (
  venueId,
  { localDate, todayLocalDate } = {},
) => withDateParam(`/v/${venueId}/food-safety`, localDate, todayLocalDate)

/** Section link from Home — same rule as `foodSafetyHomeHref`. */
export const foodSafetySectionHref = (
  venueId,
  section,
  { localDate, todayLocalDate } = {},
) =>
  withDateParam(
    `/v/${venueId}/food-safety/section/${section}`,
    localDate,
    todayLocalDate,
  )

/** Section back link: "Today’s overview" today, "Back to overview" otherwise. */
export const formatFoodSafetyOverviewBackLabel = (localDate, todayLocalDate) =>
  isFoodSafetyViewingToday(localDate, todayLocalDate)
    ? 'Today’s overview'
    : 'Back to overview'

/**
 * Schedule card badge for fridge / cleaning units (29.65).
 * Never “Missed” on off-day (`due: false`) or before due time (`not_due_yet`).
 * @returns {{ label: string, tone: 'missed'|'quiet'|'open' }|null}
 */
export function resolveFoodSafetyScheduleBadge(item, { isNext = false } = {}) {
  if (item?.completed) return null
  const exp = item?.expectation
  if (!exp || exp.due === false) return null
  if (exp.status === 'missed') return { label: 'Missed', tone: 'missed' }
  if (exp.status === 'not_due_yet') {
    return { label: 'Not due yet', tone: 'quiet' }
  }
  if (isNext) return { label: 'Open', tone: 'open' }
  return null
}

/** “today” vs “this day” for mid-sentence copy. */
export const formatFoodSafetyDayScopeWord = (viewingToday = true) =>
  viewingToday ? 'today' : 'this day'

/** “1 log today” / “3 logs this day”. */
export const formatFoodSafetyLogCountPhrase = (
  logCount,
  { viewingToday = true } = {},
) => {
  const n = Number(logCount) || 0
  if (n <= 0) return ''
  const scope = formatFoodSafetyDayScopeWord(viewingToday)
  return n === 1 ? `1 log ${scope}` : `${n} logs ${scope}`
}

/** AdHoc hero cue: “3 today” / “3 this day”. */
export const formatFoodSafetyAdHocLogCountCue = (
  logCount,
  { viewingToday = true } = {},
) => {
  const n = Number(logCount) || 0
  if (n <= 0) return ''
  return viewingToday ? `${n} today` : `${n} this day`
}

/** Empty log list on an event-driven section day. */
export const formatFoodSafetyAdHocEmptyLogs = ({ viewingToday = true } = {}) =>
  viewingToday
    ? `${formatFoodSafetyNoEntriesCue({ viewingToday })} — log when needed.`
    : `${formatFoodSafetyNoEntriesCue({ viewingToday })}.`

/** AdHoc logs list header — never “Today” on a past/future browse day. */
export const formatFoodSafetyAdHocLogsHeader = ({ viewingToday = true } = {}) =>
  viewingToday ? 'Today' : 'This day'

/**
 * One calm line under the section title.
 * Idle scheduled tiles rely on the status label alone; idle event-driven
 * tiles add “No entries today” under “Log when needed”.
 * Soft still-needed quick-pick cues never hard-block.
 * @param {{
 *   status?: string,
 *   dueCount?: number,
 *   completedCount?: number,
 *   outstandingCount?: number,
 *   missedCount?: number,
 *   logCount?: number,
 *   stillNeededCount?: number,
 *   stillNeededNames?: string[],
 *   viewingToday?: boolean,
 * }} section
 */
export const formatFoodSafetySectionDetail = section => {
  if (!section) return ''
  const {
    status,
    dueCount = 0,
    completedCount = 0,
    outstandingCount = 0,
    missedCount = 0,
    logCount = 0,
    notRequiredCount = 0,
    stillNeededCount = 0,
    stillNeededNames = [],
    unfinishedSlotLabels = [],
    activeBatchNames = [],
    activeBatchCount = 0,
    viewingToday = true,
  } = section

  const stillNeededLine = formatStillNeededHomeDetail({
    stillNeededCount,
    stillNeededNames,
    logCount,
    viewingToday,
  })
  const unfinishedSlotsLine =
    formatFridgeStillToLogFromLabels(unfinishedSlotLabels)
  const openBatchesLine = formatCoolingOpenBatchesLine(
    activeBatchNames,
    activeBatchCount,
  )

  // Soft quick-pick reminder wins over generic waiting/done copy when no scheduled dues
  if (openBatchesLine && dueCount === 0) {
    return openBatchesLine
  }
  if (stillNeededLine && dueCount === 0) {
    return stillNeededLine
  }

  if (status === FOOD_SAFETY_HOME_STATUS.IDLE) {
    if (stillNeededLine) return stillNeededLine
    return isFoodSafetySectionEventDriven(section)
      ? formatFoodSafetyNoEntriesCue({ viewingToday })
      : ''
  }

  if (status === FOOD_SAFETY_HOME_STATUS.ATTENTION) {
    // Prefer honest progress when some units are done (matches day “X of Y”).
    if (dueCount > 0 && completedCount > 0) {
      return `${completedCount} of ${dueCount} done`
    }
    if (missedCount === 1) return '1 check missed'
    return `${missedCount} checks missed`
  }
  if (status === FOOD_SAFETY_HOME_STATUS.IN_PROGRESS) {
    if (unfinishedSlotsLine) return unfinishedSlotsLine
    if (openBatchesLine) return openBatchesLine
    if (dueCount > 0) return `${completedCount} of ${dueCount} done`
    return stillNeededLine || ''
  }
  if (status === FOOD_SAFETY_HOME_STATUS.READY) {
    if (unfinishedSlotsLine) return unfinishedSlotsLine
    if (outstandingCount === 1) return '1 check waiting'
    return `${outstandingCount} checks waiting`
  }
  if (status === FOOD_SAFETY_HOME_STATUS.ALL_DONE) {
    if (stillNeededLine) return stillNeededLine
    if (dueCount > 0) return `${completedCount} of ${dueCount} done`
    if (logCount > 0) {
      return formatFoodSafetyLogCountPhrase(logCount, { viewingToday })
    }
    if (Number(notRequiredCount) > 0) {
      return Number(notRequiredCount) === 1
        ? 'Not required today'
        : `${notRequiredCount} not required`
    }
    // Never invent “All clear” with no dues and no logs
    return ''
  }
  if (stillNeededLine) return stillNeededLine
  if (logCount > 0) {
    return formatFoodSafetyLogCountPhrase(logCount, { viewingToday })
  }
  return ''
}

/**
 * Free Home history strip — proactive window + upgrade cue (Dashboard WindowHint).
 * Returns null when full history is unlocked or there is no Free window.
 * @param {{
 *   fullHistoryUnlocked?: boolean,
 *   freeHistoryDays?: number,
 *   olderRecordCount?: number,
 *   window?: object|null,
 * }|null|undefined} historyAccess
 * @returns {string|null}
 */
export const formatFoodSafetyHomeHistoryWindowHint = historyAccess => {
  if (
    !historyAccess ||
    historyAccess.fullHistoryUnlocked ||
    !historyAccess.window
  ) {
    return null
  }
  const days = Number(historyAccess.freeHistoryDays) || 14
  const older = Number(historyAccess.olderRecordCount) || 0
  const olderBit = older > 0 ? ` · ${older} older locked on Lite` : ''
  return `Showing the last ${days} days of Food Safety records${olderBit}.`
}

export const FOOD_SAFETY_HOME_HISTORY_UPGRADE_LABEL =
  'Upgrade to Starter for older days'

/**
 * Home summary under the header — chef-simple, no fake all-clear, in the
 * section cards' own words: "3 done · 3 still need a temp · 1 missed".
 * `missed` is part of `outstanding` (Express totals). Of the rest,
 * `notDueYet` is "waiting" (due later today). Anything left is "still to do".
 * Still-needed quick picks must never read as “All due checks done” /
 * “Nothing scheduled” while tiles still show work.
 * @param {object} totals
 * @param {{ viewingToday?: boolean }} [opts] — past/future browse (29.43)
 */
export const formatFoodSafetyHomeSummary = (
  totals,
  { viewingToday = true, closed = false } = {},
) => {
  if (closed) return 'Closed'
  if (!totals) return ''
  const {
    outstanding = 0,
    completedDue = 0,
    due = 0,
    logs = 0,
    skips = 0,
    missed = 0,
    notDueYet = 0,
    stillNeeded = 0,
    stillNeededNames = [],
  } = totals
  const open = Math.max(0, Number(outstanding) || 0)
  const missedCount = Math.min(open, Math.max(0, Number(missed) || 0))
  const toDo = open - missedCount
  const waiting = Math.min(toDo, Math.max(0, Number(notDueYet) || 0))
  const dueNow = toDo - waiting
  const picks = Number(stillNeeded) || 0
  const pickBit = formatFoodSafetyHomePickBit(picks, stillNeededNames)
  const skipBit = formatFoodSafetyHomeSkipBit(skips)
  const scope = formatFoodSafetyDayScopeWord(viewingToday)
  const logsBit = formatFoodSafetyLogCountPhrase(logs, { viewingToday })

  if (open > 0 || picks > 0) {
    let lead = ''
    if (Number(completedDue) > 0) {
      lead = `${completedDue} done`
    } else if (Number(due) === 0 && Number(logs) > 0) {
      lead = skipBit ? `${logsBit} · ${skipBit}` : logsBit
    }
    const line = [
      lead,
      pickBit,
      missedCount > 0 ? `${missedCount} missed` : '',
      dueNow > 0 ? `${dueNow} still to do` : '',
      waiting > 0 ? `${waiting} waiting` : '',
    ]
      .filter(Boolean)
      .join(' · ')
    return line.charAt(0).toUpperCase() + line.slice(1)
  }
  if (Number(due) > 0) {
    const done =
      Number(due) === 1 ? '1 due check done' : `All ${due} due checks done`
    return skipBit ? `${done} · ${skipBit}` : done
  }
  if (Number(logs) > 0) {
    return skipBit
      ? `${logsBit} · ${skipBit} · nothing scheduled`
      : `${logsBit} · nothing scheduled`
  }
  if (skipBit) {
    return `${skipBit} ${scope} · nothing scheduled`
  }
  return 'Nothing scheduled for this day'
}

/**
 * Still-needed cue for the day summary (lowercase mid-sentence), worded
 * like the section cards: "still need a temp for Beans" / "2 still need a
 * temp". Chef language — never “picks” (OMS internal jargon).
 */
export const formatFoodSafetyHomePickBit = (
  stillNeeded,
  stillNeededNames = [],
) => {
  const n = Number(stillNeeded) || 0
  if (n <= 0) return ''
  const names = (stillNeededNames || [])
    .map(x => String(x || '').trim())
    .filter(Boolean)
  if (n === 1 && names[0]) {
    return `still need a temp for ${names[0]}`
  }
  if (n === 1) return '1 still needs a temp'
  return `${n} still need a temp`
}

/** Not-required count — never treated as a completed temperature. */
export const formatFoodSafetyHomeSkipBit = skips => {
  const n = Number(skips) || 0
  if (n <= 0) return ''
  return n === 1 ? '1 not required' : `${n} not required`
}

/**
 * Quiet status tone for the home day-summary strip (matches section card tones).
 */
export const foodSafetyHomeSummaryTone = totals => {
  if (!totals) return FOOD_SAFETY_HOME_STATUS.IDLE
  const outstanding = Math.max(0, Number(totals.outstanding) || 0)
  const missed = Math.max(0, Number(totals.missed) || 0)
  const notDueYet = Math.max(0, Number(totals.notDueYet) || 0)
  const stillNeeded = Math.max(0, Number(totals.stillNeeded) || 0)
  const waitingOnly =
    outstanding > 0 &&
    missed === 0 &&
    stillNeeded === 0 &&
    notDueYet >= outstanding
  if (waitingOnly) return FOOD_SAFETY_HOME_STATUS.READY
  if (outstanding > 0) return FOOD_SAFETY_HOME_STATUS.ATTENTION
  if (stillNeeded > 0) return FOOD_SAFETY_HOME_STATUS.ATTENTION
  if (Number(totals.due) > 0) return FOOD_SAFETY_HOME_STATUS.ALL_DONE
  if (Number(totals.logs) > 0) return FOOD_SAFETY_HOME_STATUS.READY
  return FOOD_SAFETY_HOME_STATUS.IDLE
}
/**
 * True when the venue has no Food Safety definitions yet (manager first-run).
 */
export const isFoodSafetyHomeFirstRun = totals =>
  Boolean(totals) && Number(totals.definitions) === 0

/**
 * True when Home knows this venue has no fridge or freezer set up.
 * A missing count (older Home payload) does not guess.
 */
export const foodSafetyHomeHasNoFridges = (sections = []) => {
  const fridges = (sections || []).find(
    section => section?.section === FOOD_SAFETY_SECTION.FRIDGES_FREEZERS,
  )
  if (!fridges || fridges.definitionCount == null) return false
  return Number(fridges.definitionCount) === 0
}

/**
 * Orange nav badge: a section counts when a check is due or missed.
 * A section that is only waiting (cleaning before 22:00) does not.
 * Still-needed quick picks still count; Home drops those before the badge.
 */
export const sectionCountsForFoodSafetyTodayBadge = section => {
  if (isFoodSafetySectionWaiting(section)) return false
  return (
    Number(section?.outstandingCount) > 0 ||
    Number(section?.stillNeededCount) > 0
  )
}

/**
 * How many of today’s section tiles still need kitchen work.
 * Used for Dashboard nav / App Account orange badges (today only).
 */
export const countFoodSafetyTodayOpenSections = (sections = []) =>
  (sections || []).filter(sectionCountsForFoodSafetyTodayBadge).length

/** Short labels for the Home primary CTA (“Open Fridges — …”). */
export const FOOD_SAFETY_HOME_PRIMARY_SECTION_SHORT = Object.freeze({
  [FOOD_SAFETY_SECTION.FRIDGES_FREEZERS]: 'Fridges',
  [FOOD_SAFETY_SECTION.COOKING_REHEATING]: 'Cooking',
  [FOOD_SAFETY_SECTION.COOLING]: 'Cooling',
  [FOOD_SAFETY_SECTION.DELIVERIES]: 'Deliveries',
  [FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS]: 'Cleaning',
  [FOOD_SAFETY_SECTION.HOT_HOLDING]: 'Hot holding',
  [FOOD_SAFETY_SECTION.COLD_HOLDING]: 'Cold holding',
  [FOOD_SAFETY_SECTION.PROBE_CALIBRATION]: 'Temperature probes',
  [FOOD_SAFETY_SECTION.DISHWASHERS]: 'Dishwashers',
})

/**
 * True when a Home section tile still needs kitchen work (red / open path).
 */
export const isFoodSafetyHomeSectionOpen = section => {
  if (!section) return false
  if (Number(section.outstandingCount) > 0) return true
  if (Number(section.stillNeededCount) > 0) return true
  const status = section.status
  return (
    status === FOOD_SAFETY_HOME_STATUS.ATTENTION ||
    status === FOOD_SAFETY_HOME_STATUS.IN_PROGRESS ||
    status === FOOD_SAFETY_HOME_STATUS.READY
  )
}

/**
 * Reason phrase for the Home primary CTA (after the em dash).
 */
export const foodSafetyHomePrimaryActionReason = section => {
  if (!section) return 'open'
  if (Number(section.stillNeededCount) > 0) return 'still needed'
  if (
    section.status === FOOD_SAFETY_HOME_STATUS.ATTENTION ||
    Number(section.missedCount) > 0 ||
    Number(section.outstandingCount) > 0
  ) {
    return 'needs attention'
  }
  if (section.status === FOOD_SAFETY_HOME_STATUS.IN_PROGRESS) {
    return 'in progress'
  }
  if (section.status === FOOD_SAFETY_HOME_STATUS.READY) {
    return 'ready to check'
  }
  return 'open'
}

/**
 * Remaining open checks for Home CTA minutes (29.39).
 * Prefer outstanding, then still-needed, else remaining due.
 */
export const foodSafetyHomeOpenWorkCount = section => {
  if (!section) return 0
  const outstanding = Math.max(0, Number(section.outstandingCount) || 0)
  if (outstanding > 0) return outstanding
  const stillNeeded = Math.max(0, Number(section.stillNeededCount) || 0)
  if (stillNeeded > 0) return stillNeeded
  const due = Math.max(0, Number(section.dueCount) || 0)
  const completed = Math.max(0, Number(section.completedCount) || 0)
  if (due > 0) return Math.max(0, due - completed)
  return 0
}

/**
 * Honest kitchen minutes: ~30s per open check, min 1 when count known.
 * 4 fridges → ~2 min (plan N11 / 29.39).
 */
export const estimateFoodSafetyHomeCtaMinutes = openCount => {
  const n = Math.max(0, Number(openCount) || 0)
  if (n <= 0) return null
  return Math.max(1, Math.ceil(n * 0.5))
}

/** Compact CTA suffix — “~2 min” (not Guided Success “About 2 minutes”). */
export const formatFoodSafetyHomeCtaMinutes = minutes => {
  const n = Number(minutes)
  if (!Number.isFinite(n) || n <= 0) return null
  return n === 1 ? '~1 min' : `~${n} min`
}

/** A section whose only open checks are still before their due time. */
const isFoodSafetySectionWaiting = section => {
  const outstanding = Math.max(0, Number(section?.outstandingCount) || 0)
  const notDueYet = Math.max(0, Number(section?.notDueYetCount) || 0)
  const missed = Math.max(0, Number(section?.missedCount) || 0)
  const stillNeeded = Math.max(0, Number(section?.stillNeededCount) || 0)
  return (
    outstanding > 0 &&
    missed === 0 &&
    stillNeeded === 0 &&
    notDueYet >= outstanding
  )
}

/** Copy section not-due counts onto totals when Express has not summed them. */
export const withFoodSafetyHomeNotDueYet = (totals, sections) => {
  if (!totals || totals.notDueYet != null) return totals
  const notDueYet = (sections || []).reduce(
    (sum, section) => sum + Math.max(0, Number(section?.notDueYetCount) || 0),
    0,
  )
  return { ...totals, notDueYet }
}

/**
 * First open / attention section in Home order → one primary CTA (Q1 / 29.27).
 * A section that is only waiting (not due yet) does not lead.
 * When due/open count is known, suffix is honest minutes (29.39).
 *
 * @returns {{
 *   section: string,
 *   shortLabel: string,
 *   reason: string,
 *   openCount: number,
 *   estimatedMinutes: number|null,
 *   ctaLabel: string,
 * }|null}
 */
export const pickFoodSafetyHomePrimaryAction = sections => {
  const ordered = mergeFoodSafetyHomeSections(sections)
  const open = ordered.find(
    section =>
      isFoodSafetyHomeSectionOpen(section) &&
      !isFoodSafetySectionWaiting(section),
  )
  if (!open) return null
  const shortLabel =
    FOOD_SAFETY_HOME_PRIMARY_SECTION_SHORT[open.section] ||
    FOOD_SAFETY_SECTION_LABELS[open.section] ||
    'section'
  const reason = foodSafetyHomePrimaryActionReason(open)
  const openCount = foodSafetyHomeOpenWorkCount(open)
  const estimatedMinutes = estimateFoodSafetyHomeCtaMinutes(openCount)
  const minutesLabel = formatFoodSafetyHomeCtaMinutes(estimatedMinutes)
  return {
    section: open.section,
    shortLabel,
    reason,
    openCount,
    estimatedMinutes,
    ctaLabel: minutesLabel
      ? `Open ${shortLabel} — ${minutesLabel}`
      : `Open ${shortLabel} — ${reason}`,
  }
}

const SOFT_STILL_NEEDED_STATUSES = new Set([
  FOOD_SAFETY_HOME_STATUS.READY,
  FOOD_SAFETY_HOME_STATUS.IN_PROGRESS,
])

/**
 * Saved log-as-you-go items are logged when they happen, so an unlogged one
 * isn't work left to do. Drops Express's still-needed counts from Home
 * (summary, tiles, CTA, nav badge) and the "ready" / "in progress" status
 * it gives a tile with nothing scheduled.
 */
export const withoutFoodSafetyStillNeeded = home => {
  if (!home) return home
  const sections = (home.sections || []).map(section => {
    if (!section) return section
    const softOnly =
      Number(section.stillNeededCount) > 0 &&
      !(Number(section.dueCount) > 0) &&
      SOFT_STILL_NEEDED_STATUSES.has(section.status)
    return {
      ...section,
      stillNeededCount: 0,
      stillNeededNames: [],
      status: softOnly
        ? Number(section.logCount) > 0
          ? FOOD_SAFETY_HOME_STATUS.ALL_DONE
          : FOOD_SAFETY_HOME_STATUS.IDLE
        : section.status,
    }
  })
  return {
    ...home,
    sections,
    totals: home.totals
      ? {
          ...home.totals,
          stillNeeded: 0,
          stillNeededNames: [],
          openSections: countFoodSafetyTodayOpenSections(sections),
        }
      : home.totals,
  }
}

/**
 * True when a visible Ollie noticed row already opens the primary action's
 * section, so the page shows one button per destination.
 *
 * @param {{ section?: string }|null} primaryAction
 * @param {Array<{ action?: { path?: string } }>} rows visible noticed rows
 */
export const ollieRowCoversFoodSafetyPrimary = (primaryAction, rows) => {
  const section = primaryAction?.section
  if (!section || !Array.isArray(rows)) return false
  const target = `/food-safety/section/${encodeURIComponent(section)}`
  return rows.some(row => {
    const path = String(row?.action?.path || '').split('?')[0]
    return path.endsWith(target)
  })
}

/**
 * Badge count from a home payload — prefers Express `totals.openSections`.
 */
export const foodSafetyTodayBadgeCount = home => {
  if (!home) return 0
  if (home.totals?.openSections != null) {
    return Math.max(0, Number(home.totals.openSections) || 0)
  }
  return countFoodSafetyTodayOpenSections(home.sections)
}

export const formatFoodSafetyTodayBadgeLabel = count => {
  const n = Math.max(0, Number(count) || 0)
  if (n <= 0) return null
  if (n > 99) return '99+'
  return String(n)
}

/**
 * Hover / VoiceOver explanation for the amber open-sections badge (29.74).
 */
export const formatFoodSafetyTodayBadgeA11yLabel = count => {
  const n = Math.max(0, Number(count) || 0)
  if (n <= 0) return null
  if (n === 1) return '1 section needs work today'
  const shown = n > 99 ? '99+' : String(n)
  return `${shown} sections need work today`
}

/** V2 tiles appear only when Express sends them (V2-aware Express). */
const FOOD_SAFETY_HOME_OPTIONAL_SECTIONS = new Set([
  FOOD_SAFETY_SECTION.HOT_HOLDING,
  FOOD_SAFETY_SECTION.COLD_HOLDING,
  FOOD_SAFETY_SECTION.PROBE_CALIBRATION,
  FOOD_SAFETY_SECTION.DISHWASHERS,
])

export const mergeFoodSafetyHomeSections = apiSections => {
  const byKey = new Map((apiSections || []).map(s => [s.section, s]))
  return [
    FOOD_SAFETY_SECTION.FRIDGES_FREEZERS,
    FOOD_SAFETY_SECTION.COOKING_REHEATING,
    FOOD_SAFETY_SECTION.COOLING,
    FOOD_SAFETY_SECTION.HOT_HOLDING,
    FOOD_SAFETY_SECTION.COLD_HOLDING,
    FOOD_SAFETY_SECTION.DELIVERIES,
    FOOD_SAFETY_SECTION.PROBE_CALIBRATION,
    FOOD_SAFETY_SECTION.DISHWASHERS,
    FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS,
  ]
    .filter(
      key => !FOOD_SAFETY_HOME_OPTIONAL_SECTIONS.has(key) || byKey.has(key),
    )
    .map(key => {
      const row = byKey.get(key)
      return (
        row || {
          section: key,
          label: FOOD_SAFETY_SECTION_LABELS[key],
          status: FOOD_SAFETY_HOME_STATUS.IDLE,
          dueCount: 0,
          completedCount: 0,
          outstandingCount: 0,
          missedCount: 0,
          logCount: 0,
          stillNeededCount: 0,
          stillNeededNames: [],
          templateCount: 0,
        }
      )
    })
}

/** Visible tiles follow saved order. Hidden stay off. Anything new is appended. */
export function applyFoodSafetySectionLayout(
  sections = [],
  savedIds,
  hiddenIds = [],
) {
  if (!Array.isArray(savedIds)) return sections
  const hidden = new Set(hiddenIds || [])
  const byKey = new Map(
    (sections || []).map(section => [section.section, section]),
  )
  const placed = new Set()
  const ordered = []
  for (const id of savedIds || []) {
    const section = byKey.get(id)
    if (!section || hidden.has(id) || placed.has(id)) continue
    ordered.push(section)
    placed.add(id)
  }
  for (const section of sections || []) {
    if (placed.has(section.section) || hidden.has(section.section)) continue
    ordered.push(section)
  }
  return ordered
}
